import {
  bindSelf,
  criteriaMatch,
  normalizeCriteria,
  type CriteriaOp,
  type Criterion,
  type FilterableNode,
} from './search-criteria';

/**
 * **Custom groups** (docs/LENSES.md §6c): sections a person defines on a lens,
 * each a name plus a predicate over the shared criteria grammar ("In review" =
 * delivered AND no verdict). They organise a view and nothing else: no item
 * wears a custom group, nothing about one is stored on an item, and no event
 * is written on an item because of one. The lens's list rail shows them as
 * sections under the grouping `custom`, the board rail as lanes.
 *
 * A custom group's criteria are the same `Criterion` the filter bar edits, so
 * one editor serves both, and the one predicate (`criteriaMatch`) decides.
 * Pure: the normalizer, the key mint (randomness injected) and the match.
 */

/** One custom group: a name plus a predicate. */
export interface LensCustomGroup {
  /** A short stable id minted when the group is added; a rename keeps it, so
   *  the section arrangement keyed by it (`list.sections.custom`) survives. */
  key: string;
  /** The author's label, trimmed, 1 to {@link CUSTOM_GROUP_NAME_MAX} characters. */
  name: string;
  op: CriteriaOp;
  criteria: Criterion[];
}

/** At most this many custom groups on one lens. */
export const MAX_CUSTOM_GROUPS = 12;
/** The longest name a custom group may carry. */
export const CUSTOM_GROUP_NAME_MAX = 40;

/** A key is a short slug: it rides the `?gorder=` / `?ghide=` comma lists and
 *  the URL, so no comma, no space, and no leading underscore (the remainder
 *  section's key, `__other__`, can never collide with one). */
const KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,31}$/;

/** Is this a well-formed custom group key? */
export function isCustomGroupKey(v: unknown): v is string {
  return typeof v === 'string' && KEY_PATTERN.test(v);
}

/**
 * Coerce stored or wire input into valid custom groups, leniently like the
 * rest of the lens payload: a group with no name or with a malformed or
 * duplicate key is dropped (the first one with a key wins), the name is
 * trimmed and cut to {@link CUSTOM_GROUP_NAME_MAX}, `op` defaults to `all`,
 * the criteria go through `normalizeCriteria`, and at most
 * {@link MAX_CUSTOM_GROUPS} survive, in their defined order. Each group is
 * built from a literal in a fixed field order, so the serialization is
 * canonical (`lensPayloadsEqual`).
 */
export function normalizeCustomGroups(input: unknown): LensCustomGroup[] {
  if (!Array.isArray(input)) return [];
  const out: LensCustomGroup[] = [];
  const seen = new Set<string>();
  for (const raw of input) {
    if (out.length >= MAX_CUSTOM_GROUPS) break;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const g = raw as Record<string, unknown>;
    if (!isCustomGroupKey(g.key) || seen.has(g.key)) continue;
    const name =
      typeof g.name === 'string' ? g.name.trim().slice(0, CUSTOM_GROUP_NAME_MAX).trim() : '';
    if (name.length === 0) continue;
    seen.add(g.key);
    out.push({
      key: g.key,
      name,
      op: g.op === 'any' ? 'any' : 'all',
      criteria: normalizeCriteria(g.criteria),
    });
  }
  return out;
}

/**
 * A fresh key for a group being added: `g-` plus six base-36 characters,
 * re-drawn until it names no existing group. The random source is a
 * parameter: the editor passes `Math.random` (client code may not use
 * `crypto.randomUUID`, which the plain-http LAN address lacks; ui-kit.md),
 * tests pass a fixed sequence.
 */
export function mintCustomGroupKey(
  existing: readonly string[],
  random: () => number = Math.random,
): string {
  const taken = new Set(existing);
  for (;;) {
    let body = '';
    for (let i = 0; i < 6; i++) body += Math.floor(random() * 36).toString(36);
    const key = `g-${body}`;
    if (!taken.has(key)) return key;
  }
}

/** The custom groups as one opener evaluates them: each group's criteria
 *  through `bindSelf` with the opener's self refs, the same binding as the
 *  lens's own criteria (docs/SEARCH.md "Parties"). The stored groups keep
 *  `self`; only the evaluation binds it. Pure. */
export function bindSelfInGroups(
  groups: readonly LensCustomGroup[],
  selfRefs: readonly string[],
): LensCustomGroup[] {
  return groups.map((g) => ({ ...g, criteria: bindSelf(g.criteria, selfRefs) }));
}

/** The rows each custom group accepts, in defined order, and the rows none accepts. */
export interface CustomGroupMatches<T> {
  /** Every custom group, in defined order, with every row it accepts (possibly none). */
  groups: { group: LensCustomGroup; rows: T[] }[];
  /** The rows no custom group accepts ("Other"). */
  remainder: T[];
}

/**
 * Match rows against the custom groups (Leon, 2026-10-03): each group holds
 * EVERY row its predicate accepts, so a row two groups accept is in both -
 * the way Group by Tag shows a row under each of its tags - and the counts do
 * not add up to the rows. A row no group accepts goes to the remainder. A
 * group with no criteria accepts every row. Rows keep their incoming order
 * inside each group. `nodeOf` reads a row as the criteria see it
 * (`filterableOf` for a list row).
 */
export function matchCustomGroups<T>(
  rows: readonly T[],
  groups: readonly LensCustomGroup[],
  nodeOf: (row: T) => FilterableNode,
): CustomGroupMatches<T> {
  const matches = groups.map((group) => ({ group, rows: [] as T[] }));
  const remainder: T[] = [];
  for (const row of rows) {
    const node = nodeOf(row);
    let matched = false;
    for (const m of matches) {
      if (criteriaMatch(node, m.group.criteria, m.group.op)) {
        m.rows.push(row);
        matched = true;
      }
    }
    if (!matched) remainder.push(row);
  }
  return { groups: matches, remainder };
}
